import React, { useEffect, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import Table from './Table';
import moment from '../../../../moment';
import { useApi } from '../../../../hooks/use-api';
import * as Routes from '../../../../constants/routes';
import { useUser } from '../../../../hooks/use-user';
import { StudentDataEntry } from '../DashboardTable';

export type CertificateDataEntry = StudentDataEntry & {
	student: {
		id: number;
		displayName: string;
		establishment: {
			id: number;
		};
	};
	training: {
		name: string;
	};
	certificateExpirationDate: string;
};
const columnHelper = createColumnHelper<CertificateDataEntry>();

const getDateFormat = (date: string) => moment(date).format('L');

type Establishment = {
	id: number;
	name: string;
	company: { id: number };
};
type Props = {
	selectedEstablishmentId: number;
	establishmentData: Establishment[];
};
const CertificateRisk = ({ selectedEstablishmentId, establishmentData }: Props) => {
	const { t } = useTranslation();
	const { activeCompany } = useUser();

	const [{ data }, fetchCertificates] = useApi.get(
		Routes.getCertificatesForCompany(activeCompany.id),
		{},
		{ lazy: true },
	);

	useEffect(() => {
		fetchCertificates({ certificateExpiresBeforeDate: moment().add(6, 'months').format() });
	}, []);

	const certificates = useMemo(() => {
		if (data === null) {
			return [];
		}
		const establishmentIds =
			selectedEstablishmentId > 0
				? [selectedEstablishmentId]
				: establishmentData.map((establishment) => establishment.id);

		const filteredData = (data as CertificateDataEntry[]).filter(
			(certificate) =>
				!certificate.student.establishment?.id ||
				establishmentIds.includes(certificate.student.establishment?.id),
		);
		return filteredData.map((certificate) => ({ ...certificate, id: certificate.student.id }));
	}, [data, selectedEstablishmentId, establishmentData]);

	const columns = useMemo(
		() => [
			columnHelper.accessor('student.displayName', {
				header: t('dashboard.name'),
			}),
			columnHelper.accessor('training.name', {
				header: t('dashboard.certificate'),
			}),
			columnHelper.accessor('certificateExpirationDate', {
				header: t('dashboard.expiry_date'),
				cell: (info) => getDateFormat(info.cell.getValue()),
			}),
		],
		[t],
	);

	return <Table data={certificates} columns={columns} />;
};

export default CertificateRisk;
